import type { CountryData } from './countries';
import { useGameStore } from './store/gameStore';

type ShowToast = (opts: { message: string; type?: 'success' | 'error' | 'warning' | 'info'; duration?: number }) => void;

export class WorldQuiz {
    private readonly _showToast: ShowToast;
    private readonly _allCountries: Map<string, CountryData>;
    private readonly _allCountriesBySovereignCode: Map<string, CountryData[]> = new Map();
    private readonly _seenByCode: Set<string> = new Set();

    private _lastGuess: string | null = null;
    private _onCorrectGuess?: (countries: CountryData[]) => void;

    constructor(showToast: ShowToast, countryData: CountryData[]) {
        this._showToast = showToast;
        this._allCountries = this.buildCountryMaps(countryData);
        console.log(`Loaded ${this._allCountries.size} countries.`);
        this.updateDisplay();
    }

    private buildCountryMaps(countryData: CountryData[]): Map<string, CountryData> {
        const byId = new Map<string, CountryData>();

        for (const country of countryData) {
            byId.set(country.id, country);
            if (!this._allCountriesBySovereignCode.has(country.sovereignId)) {
                this._allCountriesBySovereignCode.set(country.sovereignId, []);
            }
            this._allCountriesBySovereignCode.get(country.sovereignId)!.push(country);
        }

        return byId;
    }

    handleGuess(guessInput: string): void {
        const guess = guessInput.trim();
        this._lastGuess = guess;

        if (!guess) {
            this._showToast({ message: 'Please enter a country name!', type: 'info' });
            return;
        }

        const id = this.getIdFromName(guess.toLowerCase());
        if (!id) {
            this._showToast({ message: `Incorrect guess: ${guess}. Try again!`, type: 'error' });
            return;
        }

        const country = this._allCountries.get(id)!;

        if (this._seenByCode.has(id)) {
            this._showToast({ message: `Already guessed ${country.name}. Try another!`, type: 'info' });
            return;
        }

        this._seenByCode.add(id);
        this._showToast({ message: `Correct! ${country.name}`, type: 'success' });
        this.updateDisplay();

        const sovereignTerritories = this._allCountriesBySovereignCode.get(country.sovereignId) ?? [country];
        this._onCorrectGuess?.(sovereignTerritories);
    }

    setOnCorrectGuess(cb: (countries: CountryData[]) => void): void {
        this._onCorrectGuess = cb;
    }

    getLastGuess(): string | null {
        return this._lastGuess;
    }

    private getIdFromName(name: string): string | null {
        for (const [id, country] of this._allCountries) {
            for (const n of country.nameList) {
                if (n.localeCompare(name, undefined, { sensitivity: 'base' }) === 0) return id;
            }
        }
        return null;
    }

    private updateDisplay(): void {
        const guessed = this._seenByCode.size;
        const total = this._allCountries.size;

        const sorted = Array.from(this._seenByCode)
            .map(code => this._allCountries.get(code)!)
            .sort((a, b) => a.name.localeCompare(b.name));

        useGameStore.getState().update(guessed, total, sorted);
    }
}
